import {
  Box,
  ButtonProps,
  Checkbox,
  FormControlLabel,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { COLORS } from "../../assets/theme";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import config from "../../config.json";
import { IField, Option, Params } from "../../types/table/interfaces.type";
import { pageAsOptions } from "../../utils/commonDataUtils";
import { useQueryClient } from "@tanstack/react-query";

import {
  BETA_WINDOW_SECONDS,
  ConnectionType,
  IConnectionProfileFilter,
  ILiveGatewayMetrics,
  useLiveGatewayMetricsQuery,
} from "../../services/LiveGatewayService";
import {
  exportSessionLogs,
  fetchAccounts,
} from "../../services/AccountService";
import Actions from "../../components/common/Actions";
import TelnetDialogContent from "../../components/TelnetDialogContent";
import CustomDialog from "../../components/common/CustomDialog";
import ToastNotification from "../../components/ToastNotification";
import { LampCell } from "../../components/CustomLamp";
import { IAutoCompleteProps } from "../../components/common/AutoComplete";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const upOnlyToStatus = (upOnly: boolean | undefined): string => {
  if (upOnly === undefined) return "";
  return upOnly ? "UP" : "DOWN";
};

const statusToUpOnly = (
  value: string | number | (string | number)[],
): boolean | undefined => {
  if (value === "UP") return true;
  if (value === "DOWN") return false;
  return undefined;
};

const LiveGatewayPage: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [appliedFilters, setAppliedFilters] = useState<
    Params<IConnectionProfileFilter>
  >({
    search: "",
    liveOnly: true,
  });
  const dialogRef = useRef<ILiveGatewayDialogRef>(null);
  const handleOpenDialog = (mode: "telnet", id?: string, rowType?: string) => {
    dialogRef.current?.openDialog(mode, id, rowType);
  };

  const queryClient = useQueryClient();
  const [cpOptions, setCpOptions] = useState<Option[]>([]);
  const [filters, setFilters] = useState<Params<IConnectionProfileFilter>>({});
  const [liveOnly, setLiveOnly] = useState<boolean>(true);
  const [beta, setBeta] = useState<boolean>(false);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const connectionProfiles = await fetchAccounts({
          size: config.MAX_PAGE_SIZE,
        });
        const cpAsOptions = pageAsOptions(connectionProfiles);
        setCpOptions(cpAsOptions);
      } catch (error: any) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
  }, []);

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      name: filters.name,
      id: filters.id,
      type: filters.type,
      liveOnly: liveOnly || undefined,
      upOnly: filters.upOnly,
    }));
  };

  const handleLiveOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setLiveOnly(checked);
    setAppliedFilters((prev) => ({
      ...prev,
      liveOnly: checked || undefined,
    }));
  };

  // Off: param not sent, so the current endpoint and interval are used
  const handleBetaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setBeta(checked);
    setAppliedFilters((prev) => ({
      ...prev,
      beta: checked || undefined,
    }));
  };

  // useEffect(() => {
  //   const intervalId = setInterval(() => {
  //     setAppliedFilters((prev) => ({ ...prev }));
  //   }, REFRESH_INTERVAL_MS);

  //   return () => {
  //     clearInterval(intervalId);
  //   };
  // }, []);

  const textFields = [
    {
      label: "Username",
      value: filters.name,
      placeholder: "Connection Name",
      helperText: "",
      error: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setFilters({ ...filters, name: e.target.value }),
      borderRadius: "8px",
    },
  ];

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Account Name",
      value: filters.id ?? "",
      options: cpOptions,
      clearable: true,
      placeholder: "Select Account",
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          id: value.toString(),
        }));
      },
    },
    {
      label: "Type",
      value: filters.type ?? "",
      clearable: true,
      options: [
        { label: "Customer", value: "CLIENT" },
        { label: "Vendor", value: "VENDOR" },
      ],
      placeholder: "Select type",
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          type: value as ConnectionType,
        }));
      },
    },
    {
      label: "Status",
      value: upOnlyToStatus(filters.upOnly),
      clearable: true,
      options: [
        { label: "Up", value: "UP" },
        { label: "Down", value: "DOWN" },
      ],
      placeholder: "Select status",
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          upOnly: statusToUpOnly(value),
        }));
      },
    },
  ];
  const actions = Actions({
    onTelnetAction: {
      handler: (row: any) => {
        console.log(`onTelnetAction action triggered for ID: ${row.id}`);
        const connectionId = String(row.id);
        const rowType = row.type;
        handleOpenDialog("telnet", connectionId, rowType);
      },
      isDisabled: !hasJwtPermission("GET_LIVE_GATEWAY_LOGS"),
    },
  });

  const buttons = [
    {
      label: "Search",
      sx: { marginTop: 2 },
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_METRICS",
    },
  ];

  const fields: IField<ILiveGatewayMetrics>[] = [
    {
      key: "id",
      header: "",
      transform: (row) => {
        const bindsValue =
          typeof row.binds === "string"
            ? row.binds
            : String(row.binds ?? "0/0");
        const firstChar = bindsValue.trim().charAt(0);
        const isUp = Number(firstChar) > 0;
        return (
          <div style={{ marginLeft: "40px" }}>
            <LampCell isUp={isUp} />
          </div>
        );
      },
    },
    { key: "type", header: "Type" },
    { key: "accountName", header: "Account Name" },
    { key: "queue", header: "Queue" },
    {
      key: "tps",
      header: beta ? `TPS Avg (${BETA_WINDOW_SECONDS}s)` : "TPS",
      // Beta TPS is an average, e.g. 4.2
      transform: (row) =>
        row.tps == null ? "" : String(Math.round(row.tps * 10) / 10),
    },
    ...(beta
      ? [
          {
            key: "minTps" as const,
            header: `TPS Min (${BETA_WINDOW_SECONDS}s)`,
          },
          {
            key: "maxTps" as const,
            header: `TPS Max (${BETA_WINDOW_SECONDS}s)`,
          },
        ]
      : []),
    { key: "failed", header: "Failed" },
    { key: "binds", header: "Binds" },
  ];

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <SearchForm
          buttons={buttons}
          textFields={textFields}
          autoCompletes={autoCompletes}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            // on mobile the checkboxes (second child) go above the search
            flexDirection: isSmallScreen ? "column-reverse" : "row",
            alignItems: isSmallScreen ? "stretch" : "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <SearchHeaderContainer
            search={appliedFilters.search ?? ""}
            setSearch={handleSearchChange}
          />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Accounts with live traffic" arrow>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={liveOnly || false}
                    onChange={handleLiveOnlyChange}
                    sx={{
                      color: COLORS.GOLD,
                      "&.Mui-checked": { color: COLORS.GOLD },
                    }}
                  />
                }
                label="Live Only"
                sx={{ whiteSpace: "nowrap" }}
              />
            </Tooltip>
            <Tooltip
              title={`Beta metrics, refreshed every ${BETA_WINDOW_SECONDS} seconds`}
              arrow
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={beta}
                    onChange={handleBetaChange}
                    sx={{
                      color: COLORS.GOLD,
                      "&.Mui-checked": { color: COLORS.GOLD },
                    }}
                  />
                }
                label="Enable Beta"
                sx={{ whiteSpace: "nowrap" }}
              />
            </Tooltip>
          </Box>
        </Box>

        <CustomTable<ILiveGatewayMetrics>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useLiveGatewayMetricsQuery}
          actions={actions}
          sortableFields={[
            "type",
            "accountName",
            "queue",
            "tps",
            "minTps",
            "maxTps",
            "failed",
            "binds",
          ]}
          initialSort={{ field: "tps", direction: "desc" }}
        />
      </Box>
      <LiveGatewayDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
      />
    </Box>
  );
};

export interface ILiveGatewayDialogRef {
  openDialog: (mode: "telnet", id?: string, rowType?: string) => void;
}

interface LiveGatewayDialogProps {
  onRefresh: () => void;
}

const LiveGatewayDialog = forwardRef<
  ILiveGatewayDialogRef,
  LiveGatewayDialogProps
>(({ onRefresh }, ref) => {
  const [telnetRowType, setTelnetRowType] = useState<string | undefined>();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<
    "add" | "edit" | "delete" | "telnet"
  >("add");
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [accountName, setAccountName] = useState<string>("");

  useImperativeHandle(ref, () => ({
    openDialog: async (
      mode: "add" | "edit" | "delete" | "telnet",
      id?: string,

      rowType?: string,
    ) => {
      setDialogMode(mode);
      setSelectedId(id);

      try {
        if (mode === "telnet") {
          setTelnetRowType(rowType);
          if (id) {
            const accounts = await fetchAccounts({ id });
            const found = accounts?.content?.find(
              (a: any) => String(a.id) === String(id),
            );
            setAccountName(found?.name || "");
          } else {
            setAccountName("");
          }
        }
        setDialogOpen(true);
      } catch (error: any) {
        console.error("Error opening dialog:", error);
        ToastNotification.error("Failed to open the dialog.");
      }
    },
  }));

  return (
    <CustomDialog
      open={dialogOpen}
      onClose={() => {
        setDialogOpen(false);
      }}
      mode={dialogMode}
      title={`${accountName} - Sessions Logs`}
      fields={
        dialogMode === "telnet"
          ? [
              {
                type: "custom",
                component: (
                  <TelnetDialogContent
                    id={String(selectedId)}
                    rowType={telnetRowType}
                    resetPermissionScope="LIVE_GATEWAY"
                    onExportClick={() => {
                      if (selectedId) {
                        exportSessionLogs({
                          connectionProfileId: Number(selectedId),
                        });
                      }
                    }}
                  />
                ),
              },
            ]
          : []
      }
      buttons={[]}
    />
  );
});

export default LiveGatewayPage;
